import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

// IMPORTS
import { Dialog, Button, TextField, MenuItem } from "@mui/material";

// CSS
import styles from "./ProjectViewAddProjectUserDialog.module.css";
import { GridSelectionModel } from "@mui/x-data-grid";
import { ProjectRoleType, UserDataRow } from "../../../../../types";
import { addProjectUser, getProjectRoles, getProjectUsers, removeProjectUser } from "../../../projectSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../app/store";

// INTERFACE
interface FormInterface {
  projectId: number;
  handleSave: any;
  handleClose: () => void;
  usersData: Array<UserDataRow>;
  add: boolean;
  edit: boolean;
  userToEdit?: UserDataRow[];
  gridSelectionModel: GridSelectionModel;
}

export default function ProjectViewAddProjectUserDialog(props: FormInterface) {
  // COMMONS
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const usersData = props.usersData;
  const projectRoles = useSelector((state: RootState) => state.selectedProject.projectRoles);

  const userToEdit = props.userToEdit !== undefined ? props.userToEdit[0] : null;

  useEffect(() => {
    if (props.edit === true && userToEdit) {
    setselectRole({id: userToEdit.projectRoleId.toString(), name: userToEdit.role});
    setuserEmail(userToEdit.email);
    }
    return () => {
      setuserEmail(undefined);
      setselectRole(undefined);
    }
  }, [props.edit, userToEdit]);

  useEffect(() => {
    if (props.projectId >= 0) {
      dispatch(getProjectRoles());
    }
    else
      setErrorMessage(
        t("projectview_page.settings_page.users_page.add_form.error_false_projectid") +
          ", " +
          props.projectId
      );
  }, [props.projectId, dispatch, t]);

  // **********************************************************************************************************************
  // FORM DATA

  const [userEmail, setuserEmail] = useState<string | undefined>(undefined);
  const [selectRole, setselectRole] = useState<ProjectRoleType | undefined>(undefined);

  //ERROR

  const [errorMessage, setErrorMessage] = useState("");

  function validateEmail(email: string) {
    var re = /\S+@\S+\.\S+/;
    return re.test(email);
  }

  // **********************************************************************************************************************

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let selectedRole = projectRoles.find(x => x.id === event.target.value);
    setselectRole(selectedRole);
  };

  const handleSave = async () => {
    if (usersData.filter(row => row.email === userEmail).length > 0) {
      setErrorMessage(t("projectview_page.settings_page.users_page.add_form.user_already_exists"));
      return;
    }
    if (userEmail === undefined || selectRole === undefined) {
      setErrorMessage(t("projectview_page.settings_page.users_page.add_form.please_fill_required"));
      return;
    } else if (props.projectId < 0) {
      setErrorMessage(
        t("projectview_page.settings_page.users_page.add_form.error_false_projectid") +
          ", " +
          props.projectId
      );
      return;
    } else if (validateEmail(userEmail) === false) {
      setErrorMessage(
        t("projectview_page.settings_page.users_page.add_form.error_email_isnot_valid")
      );
      return;
    }

    const timeNow = moment.now();

    const timeUtc = moment.utc(timeNow, true);

    let newUserDTO = {
      username: userEmail,
      projectId: props.projectId,
      projectRoleId: Number(selectRole.id),
      startDateTime: moment(timeUtc, true).format("YYYY-MM-DDTHH:mm:ss")
    };

    try {
      await dispatch(addProjectUser(newUserDTO));
    }
    catch (err) {
      console.log(err);
    }
    finally {
      dispatch(getProjectUsers(props.projectId));
      props.handleSave();
    }
  };

  const handleEditDialogSave = async (email: string | undefined, role: ProjectRoleType | undefined) => {  

    if (usersData.filter(row => row.email === email && row.id !== props.gridSelectionModel[0]).length > 0) {
      setErrorMessage(t("projectview_page.settings_page.users_page.add_form.user_already_exists"));
      return;
    }
    if (email === undefined || role === undefined) {
      setErrorMessage(t("projectview_page.settings_page.users_page.add_form.please_fill_required"));
      return;
    } else if (props.projectId < 0) {
      setErrorMessage(
        t("projectview_page.settings_page.users_page.add_form.error_false_projectid") +
          ", " +
          props.projectId
      );
      return;
    } else if (validateEmail(email) === false) {
      setErrorMessage(
        t("projectview_page.settings_page.users_page.add_form.error_email_isnot_valid")
      );
      return;
    }

    let selectedUserId = props.gridSelectionModel[0];

    const oldRole = props.usersData.filter(user => user.id === selectedUserId);

    const timeNow = moment.now();

    const timeUtc = moment.utc(timeNow, true);

    let endTimeUserDTO = {
        id: Number(selectedUserId),
        username: oldRole[0].email,
        projectId: props.projectId,
        projectRoleId: oldRole[0].projectRoleId,
        endDateTime: moment(timeUtc, true).format("YYYY-MM-DDTHH:mm:ss")
      };

    let newUserDTO = {
        username: email,
        projectId: props.projectId,
        projectRoleId: Number(role.id),
        startDateTime: moment(timeUtc, true).format("YYYY-MM-DDTHH:mm:ss")
      };

      try {
        await dispatch(removeProjectUser(endTimeUserDTO));
        await dispatch(addProjectUser(newUserDTO));
      }
      catch (err) {
        console.log(err);
      }
      finally {
        dispatch(getProjectUsers(props.projectId));
        props.handleSave();
      }
  };

  const handleClose = () => {
    props.handleClose();
  };

  return (
    <Dialog open={true} onClose={handleClose}>
      <div className={styles["adduser-form-container"]}>
        {/**************************************************************************** */}

        <div className={styles["adduser-form-header"]}>
          {t("projectview_page.settings_page.users_page.add_form.add_new_user")}
        </div>
        {/* *************************************************************************** */}

        <TextField
          label={t("projectview_page.settings_page.users_page.add_form.user_email")}
          variant="standard"
          fullWidth
          required
          value={userEmail !== undefined ? userEmail : ""}
          type="email"
          onChange={event => setuserEmail(event.target.value)}
          InputLabelProps={{ style: { fontSize: "var(--inputFontSize)" } }}
          InputProps={{ style: { fontSize: "var(--inputFontSize)" } }}
        />
        <TextField
          label={t("projectview_page.settings_page.users_page.add_form.user_role")}
          variant="standard"
          fullWidth
          required
          select
          value={selectRole !== undefined ? selectRole.id : ""}
          onChange={handleRoleChange}
          InputLabelProps={{ style: { fontSize: "var(--inputFontSize)" } }}
          InputProps={{ style: { fontSize: "var(--inputFontSize)" } }}
        >
          {projectRoles.map(role => {
            return (
              <MenuItem
                sx={{ fontSize: "var(--inputFontSize)" }}
                key={"projectroles_" + role.id}
                value={role.id}
              >
                {role.name}
              </MenuItem>
            );
          })}
        </TextField>
        {/* *************************************************************************** */}

        <Button
          variant="contained"
          color="error"
          style={errorMessage !== "" ? { display: "" } : { display: "none" }}
        >
          {errorMessage}
        </Button>
        <Button variant="contained" onClick={props.add ? () => handleSave() : () => handleEditDialogSave(userEmail, selectRole)}>
          {t("common.save")}
        </Button>
        <Button variant="contained" onClick={() => handleClose()}>
          {t("common.cancel")}
        </Button>
      </div>
    </Dialog>
  );
}
